import { QueryClient } from '@tanstack/react-query';
import { UserProgress } from '../services/progressService';
import { clearNotesCache, getCachedNotes } from '../services/cacheService';

export const PROGRESS_QUERY_KEY = ['progress'] as const;

export const progressQueryKey = (userId: string) => [...PROGRESS_QUERY_KEY, userId] as const;

// Builds the lookup key used in the progress map returned by
// progressService.batchFetchAllUserProgress. Accepts collection paths with or
// without a leading slash ("/courses/x/children" from course pages,
// "courses/x/children" from My Notes).
export const buildProgressKey = (docId: string, collectionPath?: string): string => {
    const cleanCollectionPath = collectionPath?.startsWith('/') ? collectionPath.substring(1) : collectionPath;
    return cleanCollectionPath
        ? `${cleanCollectionPath}/${docId}`
        : `courses/${docId}`;
};

// Writes a freshly fetched progress record into the cached progress map so
// every card reflects the save without refetching all of the user's progress.
// If progress hasn't been loaded yet (or failed), it is marked stale instead so
// the next read fetches it.
export const setCachedProgress = (
    queryClient: QueryClient,
    userId: string,
    docId: string,
    collectionPath: string | undefined,
    progress: UserProgress | null
) => {
    const queryKey = progressQueryKey(userId);
    if (!queryClient.getQueryData(queryKey)) {
        queryClient.invalidateQueries({ queryKey });
        return;
    }

    queryClient.setQueryData<Map<string, UserProgress>>(queryKey, (previous: Map<string, UserProgress> | undefined) => {
        if (!previous) return previous;

        const next = new Map(previous);
        const key = buildProgressKey(docId, collectionPath);
        if (progress) {
            next.set(key, progress);
        } else {
            next.delete(key);
        }
        return next;
    });
};

// My Notes keeps its own 24h localStorage cache. Clear it only when a save can
// change what My Notes shows: the lecture has a note now, or was already listed.
// Completion/favourite changes on lectures without notes leave it untouched.
export const clearNotesCacheIfAffected = (
    userId: string,
    docId: string,
    collectionPath: string | undefined,
    progress: UserProgress | null
) => {
    const key = buildProgressKey(docId, collectionPath);
    const hasNote = !!progress?.notes;
    const isListedInMyNotes = !!getCachedNotes(userId)?.notes.some(note => note.parentDocPath === key);

    if (hasNote || isListedInMyNotes) {
        clearNotesCache();
    }
};

// Keeps every client-side cache in step after a progress save.
export const syncCachesAfterSave = (
    queryClient: QueryClient,
    userId: string,
    docId: string,
    collectionPath: string | undefined,
    progress: UserProgress | null
) => {
    setCachedProgress(queryClient, userId, docId, collectionPath, progress);
    clearNotesCacheIfAffected(userId, docId, collectionPath, progress);
};
